<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	variant: $.select(
		'Page Type',
		[
			{ label: 'Topic', value: 'topic' },
			{ label: 'Book', value: 'book' },
			{ label: 'Holiday', value: 'holiday' },
			{ label: 'Torah Portion', value: 'parashah' },
			{ label: 'Category', value: 'category' },
		],
		{ default: 'category' },
	),
	section: $.select(
		'Page Section',
		[
			{ label: 'Header and metadata', value: 'header' },
			{ label: 'Groups and article feed', value: 'content' },
		],
		{ default: 'content' },
	),
})
export const EntityPage = defineBlock({
	thumbnail: '/block-thumbs/EntityPage.svg',
	category: 'Listings',
	order: 125,
	name: 'EntityPage',
	label: 'Entity Collection Page',
	icon: 'collections_bookmark',
	schema,
})
</script>
<script setup lang="ts">
import { computed } from 'vue'

import { Image, Skeleton } from '@bagelink/vue'
import AuthorSmallLink from '@/components/blocks/AuthorSmallLink.vue'
import BlockArticleList from '@/components/blocks/BlockArticleList.vue'
import { useBoundPageItem } from '@/composables/useBoundPageItem'
import { useCollection } from '@/composables/useDatastore'
import { articleTitleText, sanitizeArticleHtml, sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { displayImage } from '@/utils/image'

type EntityVariant = 'topic' | 'book' | 'holiday' | 'parashah' | 'category'

interface Article {
	_id?: string
	title?: string
	slug?: string
	image?: string
	authorIds?: string[]
	authorId?: string
	secondAuthorId?: string
	thirdAuthorId?: string
	parshaIds?: string[]
	categoryIds?: string[]
	holidayIds?: string[]
	topicIds?: string[]
	publishDate?: string
	hits?: number
	teaser?: string
}

interface Group {
	_id?: string
	name?: string
	slug?: string
	articleIds?: string[]
	articles?: string[]
	topicIds?: string[]
	seriesId?: string
	symposiumId?: string
	symposium?: string
	parentCategoryId?: string
	holiday?: string
	orderOfAppearance?: number
	torahPortion?: string
	hits?: number
}

interface Parashah {
	_id?: string
	name?: string
	slug?: string
	bookId?: string
	orderInBook?: number
}

/** A group that stands for a series/symposium renders a collection card instead. */
interface CollectionCard {
	type: 'Series' | 'Symposium'
	title: string
	link: string
	image: string
}



const props = defineProps<{ variant?: EntityVariant; section?: 'header' | 'content' }>()
const entity = useBoundPageItem<Record<string, any>>()

// Book and Torah-portion pages resolve `topicIds` → names, so they need the whole
// topics collection (~1.1k rows). A fixed page limit silently dropped any topic
// past it, which blanked names in the list. The other variants only pick the
// best-performing topics for the sidebar, so they keep the smaller page.
const needsAllTopics = props.variant === 'book' || props.variant === 'parashah'
const { items: topics, loading: topicsLoading } = useCollection<Group>(
	'topics',
	needsAllTopics
		? { limit: 1000, all: true, pick: ['_id', 'name', 'slug', 'hits'] }
		: { limit: 500, pick: ['_id', 'name', 'slug', 'hits'] },
)
const { items: subcategories, loading: subcategoriesLoading } = useCollection<Group>(
	'subcategories',
	{ limit: 1000 },
)
const { items: holidayGroups, loading: holidayGroupsLoading } = useCollection<Group>(
	'holiday-groups',
	{ limit: 1000 },
)
const { items: series, loading: seriesLoading } = useCollection<Record<string, any>>('series', {
	limit: 100,
	pick: ['id', '_id', 'title', 'slug', 'image'],
})
const { items: symposia, loading: symposiaLoading } = useCollection<Record<string, any>>(
	'symposia',
	{
		limit: 100,
		// Only the collection card's fields — a symposium's `content` is a long
		// HTML body that would otherwise ride along in every entity page payload.
		pick: ['id', '_id', 'title', 'slug', 'image'],
	},
)
const { items: parashahs, loading: parashahsLoading } = useCollection<Parashah>('parashahs', {
	limit: 100,
	pick: ['id', '_id', 'name', 'slug', 'bookId', 'orderInBook'],
})

const variant = computed<EntityVariant>(() => props.variant || 'category')
const title = computed(() => entity.value?.name || entity.value?.title || '')
const hebrewTitle = computed(() => entity.value?.hebrewName || '')
const section = computed(() => props.section || 'content')

/**
 * Prerendered entity pages embed their own essays (`_ssgArticles`). Without that
 * snapshot — local dev, or a template page loaded straight from the API — a
 * category's group grids have nothing to list, so the page degraded into just the
 * series/symposium cards. Fill the pool from the collection instead: the fallback
 * is client-only and is skipped wherever the snapshot is present, so the
 * prerendered payload keeps carrying the articles exactly as before.
 */
const { items: fallbackArticles } = useCollection<Article>('articles', {
	limit: 1000,
	all: true,
	clientOnly: true,
	enabled: () => variant.value === 'category' && !Array.isArray(entity.value?._ssgArticles),
	pick: [
		'_id',
		'slug',
		'title',
		'teaser',
		'image',
		'publishDate',
		'topicIds',
		'authorIds',
		'authors',
	],
})
const contentLoading = computed(
	() =>
		topicsLoading.value ||
		subcategoriesLoading.value ||
		holidayGroupsLoading.value ||
		seriesLoading.value ||
		symposiaLoading.value ||
		parashahsLoading.value,
)

const bookParashahs = computed(() => {
	if (variant.value !== 'book') return []

	// The collection also stores the combined ("double") readings — e.g.
	// "Vayakhel-Pekudei" — as extra rows without an `orderInBook`. They are a
	// schedule variant of two portions, not an additional one, so the book index
	// lists only the 54 individual portions.
	const linked = parashahs.value
		.filter(
			parashah => parashah.bookId === entity.value?._id && typeof parashah.orderInBook === 'number',
		)
		.sort((a: Parashah, b: Parashah) => (a.orderInBook ?? 0) - (b.orderInBook ?? 0))

	if (linked.length) {
		const bySlug = new Map<string, { name: string; slug: string }>()
		for (const parashah of linked) {
			const slug = parashah.slug || ''
			if (!slug || bySlug.has(slug)) continue
			bySlug.set(slug, { name: parashah.name || '', slug })
		}
		return [...bySlug.values()]
	}

	return []
})

const groupIds = computed<string[]>(() => {
	if (variant.value === 'holiday') {
		return holidayGroups.value
			.filter(group => group.holiday === entity.value?.name)
			.sort((a, b) => (a.orderOfAppearance ?? 0) - (b.orderOfAppearance ?? 0))
			.map(group => group._id)
			.filter((id): id is string => !!id)
	}
	if (variant.value === 'book' || variant.value === 'category')
		return entity.value?.topicGroupIds ?? []
	if (variant.value === 'parashah') return entity.value?.topicIds ?? []
	return []
})

const groups = computed<Group[]>(() => {
	const source =
		variant.value === 'holiday'
			? holidayGroups.value
			: variant.value === 'parashah'
				? topics.value
				: subcategories.value
	const byId = new Map(source.map(group => [group._id, group]))
	const ordered = groupIds.value.map(id => byId.get(id)).filter((group): group is Group => !!group)
	if (variant.value !== 'category' || !entity.value?._id) return ordered
	const seen = new Set(ordered.map(group => group._id))
	// A child subcategory that stands for a series/symposium already represented in
	// the curated order is the same card — appending it printed the collection twice.
	const represented = new Set(
		ordered.map(group => group.symposiumId || group.seriesId).filter((id): id is string => !!id),
	)
	return [
		...ordered,
		...subcategories.value.filter(
			group =>
				group.parentCategoryId === entity.value?._id &&
				!seen.has(group._id) &&
				!represented.has(group.symposiumId || group.seriesId || ''),
		),
	]
})

const related = computed<Article[]>(() => {
	const embedded = entity.value?._ssgArticles
	if (Array.isArray(embedded)) return embedded
	// Only categories fall back to the collection: their essays are selected per
	// group (`articleIds` / `topicIds`), so one pool resolves every section.
	return variant.value === 'category' ? fallbackArticles.value : []
})

function toCard(article: Article) {
	return {
		title: article.title || '',
		subtitle: article.teaser || '',
		image: article.image || '/og.jpg',
		link: `/article/${article.slug}`,
		authors: (article as any).authors ?? [],
	}
}

const topicArticles = computed(() =>
	[...related.value]
		.sort((a, b) => new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime())
		.map(toCard),
)

const browseTopics = computed(() =>
	topics.value
		.filter(topic => topic._id !== entity.value?._id && topic.name && topic.slug)
		.sort((a, b) => (b.hits ?? 0) - (a.hits ?? 0))
		.slice(0, 7)
		.map(topic => ({ name: topic.name || '', link: `/topic/${topic.slug}` })),
)

const parashahTopics = computed(() => {
	if (variant.value !== 'parashah') return []
	const byId = new Map(topics.value.map(topic => [topic._id, topic]))
	const topicIds: string[] = entity.value?.topicIds ?? []
	return topicIds
		.map((id: string) => byId.get(id))
		.filter((topic: Group | undefined): topic is Group => !!topic?.name && !!topic?.slug)
		.map((topic: Group) => ({ name: topic.name || '', link: `/topic/${topic.slug}` }))
})

function articlesForGroup(group: Group) {
	const explicitIds = new Set([...(group.articleIds ?? []), ...(group.articles ?? [])])
	const topicIds = new Set(
		group.topicIds ?? (variant.value === 'parashah' && group._id ? [group._id] : []),
	)
	return related.value
		.filter(
			article =>
				explicitIds.has(article._id || '') ||
				article.topicIds?.some(topicId => topicIds.has(topicId)),
		)
		.map(toCard)
}

const topicById = computed(() => new Map(topics.value.map(topic => [topic._id, topic])))

/**
 * A book page is a *topic index*, matching the live site: every group lists the
 * topics that belong to it (`subcategories.topicIds`, in curated order) and the
 * essays live one click away on the topic pages. Rendering the essays inline
 * here made the page a second essay feed instead of a way into the topics.
 */
function topicLinksForGroup(group: Group) {
	if (variant.value !== 'book') return []
	return (group.topicIds ?? [])
		.map(id => topicById.value.get(id))
		.filter((topic): topic is Group => !!topic?.name && !!topic?.slug)
		.map(topic => ({ name: topic.name as string, link: `/topic/${topic.slug}` }))
}

const visibleGroups = computed(() =>
	groups.value.filter(
		group =>
			// A group that resolves to a series/symposium is a collection card even when
			// none of its essays are inlined here. Those subcategories list no
			// articleIds/topicIds, so requiring an article dropped every symposium and
			// series block from the category pages.
			collectionOf(group) ||
			(variant.value === 'book'
				? topicLinksForGroup(group).length > 0
				: articlesForGroup(group).length > 0),
	),
)

function collectionForGroup(group: Group): CollectionCard | null {
	if (group.seriesId) {
		const item = series.value.find(entry => entry._id === group.seriesId)
		return item
			? {
					type: 'Series',
					title: item.title || group.name,
					link: `/series/${item.slug}`,
					image: item.image || '/og.jpg',
				}
			: null
	}
	const symposiumId = group.symposiumId || group.symposium
	if (symposiumId) {
		const item = symposia.value.find(entry => entry._id === symposiumId)
		return item
			? {
					type: 'Symposium',
					title: item.title || group.name,
					link: `/symposium/${item.slug}`,
					image: item.image || '/og.jpg',
				}
			: null
	}
	return null
}

const groupCollections = computed(() => {
	const map = new Map<string, CollectionCard>()
	for (const group of groups.value) {
		const id = group._id
		if (!id) continue
		const collection = collectionForGroup(group)
		if (collection) map.set(id, collection)
	}
	return map
})

function collectionOf(group: Group): CollectionCard | null {
	return group._id ? (groupCollections.value.get(group._id) ?? null) : null
}

function scrollToGroup(group: Group) {
	const id = group.slug || group._id
	if (!id) return
	const target = document.getElementById(id)
	if (!target) return
	target.scrollIntoView({ behavior: 'smooth', block: 'start' })
	window.history.replaceState(
		null,
		'',
		`${window.location.pathname}${window.location.search}#${id}`,
	)
}
</script>

<template>
	<main :class="section === 'content' ? 'pb-4 m_pb-2' : ''">
		<header v-if="section === 'header'" class="w1100px txt-center pt-3 pb-2 m_px-1 m_pt-2 m_pb-1">
			<p v-if="variant === 'book'" class="m-0 txt13 color-gray-70 pb-05">The Book of</p>
			<p v-else-if="variant === 'parashah'" class="m-0 txt13 color-gray-70 pb-1">Torah Portion</p>
			<h1
				class="m-0 regular line-height-11 balance m_line-height-12 gap-1 m_gap-075 flex align-items flex-wrap justify-content-center pb-1"
			>
				<span class="txt-40 m_txt-32 pt-025">{{ title }}</span>
				<span v-if="hebrewTitle" class="txt-49 m_txt-38" dir="rtl">{{ hebrewTitle }}</span>
			</h1>
			<div
				v-if="variant === 'category' && entity?.teaser"
				class="w720px m_w-100p mx-auto pt-1-5 txt17 line-height-18 pretty m_txt1"
				v-html="sanitizeArticleHtml(entity.teaser)"
			/>
			<div
				v-if="variant === 'book' && parashahsLoading"
				class="flex flex-wrap justify-content-center gap-1 pt-1-5 m_gap-075 m_pt-1"
			>
				<Skeleton v-for="item in 12" :key="item" width="72px" height="18px" />
			</div>
			<nav
				v-else-if="bookParashahs.length"
				:aria-label="`${title} Torah portions`"
				class="flex flex-wrap justify-content-center gap-2 pt-1-5 m_gap-075 m_pt-1"
			>
				<RouterLink
					v-for="parashah in bookParashahs"
					:key="parashah.slug"
					:to="`/parashah/${parashah.slug}`"
					class="decoration-none hover-underline-short txt18 italic min-w-60px"
				>
					{{ parashah.name }}
				</RouterLink>
			</nav>
			<div
				v-if="variant === 'parashah' && (entity?.torahPortion || entity?.haftarah)"
				class="flex flex-wrap justify-content-center gap-2 pt-1 m_gap-1"
			>
				<i v-if="entity?.torahPortion" class="txt17 color-gray-70">
					{{ entity.torahPortion }}
				</i>
				<i v-if="entity?.haftarah" class="txt17 color-gray-70">
					{{ entity.haftarah }}
				</i>
			</div>
		</header>

		<div
			v-if="section === 'content' && variant === 'parashah' && contentLoading"
			class="w1000px py-2 m_px-1"
			aria-label="Loading Torah portion articles"
			aria-busy="true"
		>
			<div class="flex justify-content-center gap-1 pb-3">
				<Skeleton v-for="topic in 4" :key="topic" width="100px" height="20px" />
			</div>
			<div class="w720px mx-auto grid gap-2">
				<div v-for="article in 5" :key="article" class="display-flex gap-1">
					<Skeleton width="150px" height="110px" class="flex-shrink-0" />
					<div class="flex-grow">
						<Skeleton width="90%" height="22px" class="mb-05" />
						<Skeleton width="100%" height="16px" class="mb-025" />
						<Skeleton width="75%" height="16px" class="mb-05" />
						<Skeleton width="45%" height="14px" />
					</div>
				</div>
			</div>
		</div>

		<div v-else-if="section === 'content' && variant === 'parashah'">
			<nav
				v-if="parashahTopics.length"
				:aria-label="`${title} topics`"
				class="flex flex-wrap justify-content-center gap-2 pt-1 pb-2 m_gap-1 m_px-1 w1000px"
			>
				<strong class="txt17">Topics:</strong>
				<RouterLink
					v-for="topic in parashahTopics"
					:key="topic.link"
					:to="topic.link"
					class="decoration-none hover-underline-short txt17 italic min-w-60px txt-center"
				>
					{{ topic.name }}
				</RouterLink>
			</nav>
			<BlockArticleList
				:articles="topicArticles"
				show-topics="false"
				show-authors="true"
				class="m_px-1"
			/>
		</div>

		<div
			v-else-if="section === 'content' && variant === 'topic' && contentLoading"
			class="w1000px display-flex gap-3 py-2 m_px-1"
			aria-label="Loading topic articles"
			aria-busy="true"
		>
			<div class="w720px grid gap-2">
				<div v-for="article in 5" :key="article" class="display-flex gap-1">
					<Skeleton width="150px" height="110px" class="flex-shrink-0" />
					<div class="flex-grow">
						<Skeleton width="90%" height="22px" class="mb-05" />
						<Skeleton width="100%" height="16px" class="mb-025" />
						<Skeleton width="75%" height="16px" class="mb-05" />
						<Skeleton width="45%" height="14px" />
					</div>
				</div>
			</div>
			<div class="desktop-only">
				<Skeleton width="120px" height="22px" class="mb-2" />
				<Skeleton v-for="topic in 7" :key="topic" width="110px" height="18px" class="mb-1" />
			</div>
		</div>

		<BlockArticleList
			v-else-if="section === 'content' && variant === 'topic'"
			:articles="topicArticles"
			:topics="browseTopics"
			show-topics="true"
			show-authors="true"
		/>

		<div
			v-else-if="section === 'content' && contentLoading"
			class="w1100px px-1 pt-3 m_px-075 m_pt-2"
			aria-label="Loading Explore Topics"
			aria-busy="true"
		>
			<div class="entity-content grid gap-1 m_gap-1 m_grid-template-columns-1">
				<aside class="bg-gray-100 p-1-25 m_hide">
					<Skeleton width="150px" height="30px" class="mb-1-5" />
					<Skeleton v-for="topic in 8" :key="topic" width="85%" height="20px" class="mb-1" />
				</aside>

				<div>
					<Skeleton width="100%" height="170px" class="mb-1-5" />
					<Skeleton width="100%" height="44px" class="mb-1-5" />
					<div class="grid grid-wrap-2 col-gap-2 row-gap-2 m_grid-wrap-1 m_gap-1-5">
						<div v-for="article in 6" :key="article" class="display-flex gap-075">
							<Skeleton width="64px" height="64px" class="flex-shrink-0" />
							<div class="flex-grow min-w-0">
								<Skeleton width="95%" height="20px" class="mb-025" />
								<Skeleton width="75%" height="20px" class="mb-05" />
								<Skeleton width="55%" height="14px" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div
			v-else-if="section === 'content' && visibleGroups.length"
			id="explore-topics"
			class="w1000px px-1 pt-3 m_px-1 m_pt-2"
		>
			<div class="entity-content grid gap-1 m_gap-1 m_grid-template-columns-1">
				<aside class="m_hide">
					<div class="sticky-4 bg-gray-100" style="--bgl-scrollbar-thumb: var(--bgl-gray-110)">
						<h2 class="m-0 txt24 regular line-height-13 balance p-1-25 m_txt20 m_pb-075">
							Explore Topics:
						</h2>
						<nav
							class="grid gap-1-25 m_display-flex m_flex-wrap-nowrap m_gap-1 overflow p-1-25 max-h-side"
						>
							<a
								v-for="group in visibleGroups"
								:key="group._id"
								:href="`#${group.slug || group._id}`"
								class="decoration-none hover flex-shrink-0 txt16 line-height-13"
								@click.prevent="scrollToGroup(group)"
							>
								{{ group.name }}
							</a>
						</nav>
					</div>
				</aside>

				<div class="grid align-content-start">
					<section
						v-for="group in visibleGroups"
						:id="group.slug || group._id"
						:key="group._id"
						class="topic-section pb-2 m_pb-2"
					>
						<RouterLink
							v-if="collectionOf(group)"
							:to="collectionOf(group)!.link"
							class="display-flex bg-gray-100 decoration-none mb-1-5 m_column"
						>
							<Image
								:src="displayImage(collectionOf(group)!.image, 'card')"
								:alt="collectionOf(group)!.title"
								ratio="4-3"
						class="w-30p cover flex-shrink-0 m_w-100p"
						lazy
					/>
							<div class="p-1-5 m_p-1">
								<p class="m-0 txt14 color-primary pb-05">
									{{ collectionOf(group)!.type }}
								</p>
								<h3 class="m-0 txt28 regular line-height-13 balance m_txt22 m_line-height-13">
									{{ collectionOf(group)!.title }}
								</h3>
							</div>
						</RouterLink>

						<!-- The collection card already carries the group title, so the
						     heading would repeat it (the live site hides it too). -->
						<h3
							v-if="!collectionOf(group)"
							class="mt-0 txt24 regular line-height-13 balance bg-gray-100 py-05 px-1 mb-1-5 m_txt20 m_mb-1"
						>
							{{ group.name }}
						</h3>
						<div
							v-if="variant === 'book' && topicLinksForGroup(group).length"
							class="grid grid-wrap-2 col-gap-2 row-gap-1 m_grid-wrap-2 m_gap-05 px-1"
						>
							<RouterLink
								v-for="topic in topicLinksForGroup(group)"
								:key="topic.link"
								:to="topic.link"
								class="decoration-none hover-underline-short1 txt18 regular line-height-13"
							>
								{{ topic.name }}
							</RouterLink>
						</div>
						<div
							v-else-if="articlesForGroup(group).length"
							class="grid grid-wrap-2 col-gap-2 row-gap-2 m_grid-wrap-1 m_gap-1-5"
						>
							<article
								v-for="article in articlesForGroup(group)"
								:key="article.link"
								class="display-flex align-items-start gap-075 min-w-0"
							>
								<RouterLink :to="article.link" class="flex-shrink-0">
									<Image
										:src="displayImage(article.image, 'thumb')"
										:alt="articleTitleText(article.title)"
										ratio="1"
								class="square-64 cover"
								lazy
							/>
								</RouterLink>
								<div class="min-w-0">
									<RouterLink :to="article.link" class="decoration-none">
							<h4
								class="m-0 txt18 regular line-height-16 balance pb-025"
								v-html="sanitizeArticleTitleHtml(article.title)"
							/>
									</RouterLink>
									<AuthorSmallLink
										v-for="author in article.authors"
										:key="author.name"
										:author="author"
										:show-image="false"
									/>
								</div>
							</article>
						</div>
					</section>
				</div>
			</div>
		</div>
	</main>
</template>

<style scoped>
.entity-content {
	grid-template-columns: 220px minmax(0, 1fr);
}

.topic-section {
	scroll-margin-top: 4rem;
}
</style>
